/* DD hero entrance and magnetic CTA effects with scoped lifecycle cleanup. */

const motionOK = matchMedia('(prefers-reduced-motion: no-preference)');
const finePointer = matchMedia('(hover: hover) and (pointer: fine)');
const MAGNETIC_SELECTOR = [
  'a.button:not(.button-unstyled)',
  'a.button-secondary',
  '.button-primary',
  '[data-dd-magnetic]',
].join(', ');
const MAX_OFFSET = 6;
const LERP = 0.18;

/** @typedef {{node: HTMLElement, controller: AbortController, rafId: number, targetX: number, targetY: number, currentX: number, currentY: number, rect: DOMRect | null}} MagneticRecord */
/** @type {Map<HTMLElement, MagneticRecord>} */
const magneticRecords = new Map();

function enabled() {
  return motionOK.matches && finePointer.matches;
}

/** @param {ParentNode} root @param {string} selector */
function matchesWithin(root, selector) {
  const matches = [];
  if (root instanceof Element && root.matches(selector)) matches.push(root);
  matches.push(...root.querySelectorAll(selector));
  return matches;
}

/** @param {ParentNode} root */
function mountHero(root) {
  const main = document.querySelector('main[data-template="index"]');
  const firstSection = main?.querySelector(':scope > .shopify-section');
  if (!firstSection) return;
  if (root !== document && root !== firstSection && !(root instanceof Node && root.contains(firstSection))) return;
  const heroContent = firstSection.querySelector('.hero__content-wrapper');
  if (!heroContent) return;
  Array.from(heroContent.children).forEach((child, index) => {
    if (child instanceof HTMLElement) child.style.setProperty('--dd-stagger-i', String(index));
  });
  heroContent.setAttribute('data-dd-hero-intro', '');
}

/** @param {HTMLElement} node */
function releaseMagnetic(node) {
  const record = magneticRecords.get(node);
  if (!record) return;
  record.controller.abort();
  if (record.rafId) cancelAnimationFrame(record.rafId);
  node.classList.remove('dd-magnetic');
  node.style.removeProperty('transform');
  magneticRecords.delete(node);
}

/** @param {HTMLElement} node */
function bindMagnetic(node) {
  if (magneticRecords.has(node)) return;
  const record = {
    node,
    controller: new AbortController(),
    rafId: 0,
    targetX: 0,
    targetY: 0,
    currentX: 0,
    currentY: 0,
    rect: null,
  };
  magneticRecords.set(node, record);

  const tick = () => {
    if (!node.isConnected || !enabled()) {
      releaseMagnetic(node);
      return;
    }
    record.currentX += (record.targetX - record.currentX) * LERP;
    record.currentY += (record.targetY - record.currentY) * LERP;
    if (
      Math.abs(record.currentX) < 0.1 &&
      Math.abs(record.currentY) < 0.1 &&
      record.targetX === 0 &&
      record.targetY === 0
    ) {
      node.style.removeProperty('transform');
      record.rafId = 0;
      return;
    }
    node.style.transform = `translate(${record.currentX.toFixed(2)}px, ${record.currentY.toFixed(2)}px)`;
    record.rafId = requestAnimationFrame(tick);
  };

  const start = () => {
    if (!record.rafId) record.rafId = requestAnimationFrame(tick);
  };

  node.addEventListener('pointerenter', () => {
    node.classList.add('dd-magnetic');
    record.rect = node.getBoundingClientRect();
  }, { signal: record.controller.signal });

  node.addEventListener('pointermove', (event) => {
    const bounds = record.rect ?? node.getBoundingClientRect();
    record.rect = bounds;
    record.targetX = Math.max(-MAX_OFFSET, Math.min(MAX_OFFSET, ((event.clientX - bounds.left) / bounds.width - 0.5) * MAX_OFFSET * 2));
    record.targetY = Math.max(-MAX_OFFSET, Math.min(MAX_OFFSET, ((event.clientY - bounds.top) / bounds.height - 0.5) * MAX_OFFSET * 2));
    start();
  }, { passive: true, signal: record.controller.signal });

  node.addEventListener('pointerleave', () => {
    node.classList.remove('dd-magnetic');
    record.rect = null;
    record.targetX = 0;
    record.targetY = 0;
    start();
  }, { signal: record.controller.signal });
}

/** @param {ParentNode} root */
function mount(root = document) {
  if (!enabled()) return;
  mountHero(root);
  for (const element of matchesWithin(root, MAGNETIC_SELECTOR)) {
    if (element instanceof HTMLElement) bindMagnetic(element);
  }
}

/** @param {ParentNode} root */
function unmount(root) {
  for (const node of Array.from(magneticRecords.keys())) {
    if (node === root || (root instanceof Node && root.contains(node))) releaseMagnetic(node);
  }
  for (const hero of matchesWithin(root, '[data-dd-hero-intro]')) {
    hero.removeAttribute('data-dd-hero-intro');
    for (const child of hero.children) {
      if (child instanceof HTMLElement) child.style.removeProperty('--dd-stagger-i');
    }
  }
}

function applyState() {
  if (enabled()) {
    mount(document);
    return;
  }
  for (const node of Array.from(magneticRecords.keys())) releaseMagnetic(node);
  unmount(document);
}

motionOK.addEventListener('change', applyState);
finePointer.addEventListener('change', applyState);
document.addEventListener('shopify:section:load', (event) => mount(/** @type {ParentNode} */ (event.target)));
document.addEventListener('shopify:section:unload', (event) => unmount(/** @type {ParentNode} */ (event.target)));
document.addEventListener('shopify:section:reorder', applyState);

const removalObserver = new MutationObserver((mutations) => {
  for (const mutation of mutations) {
    for (const node of mutation.removedNodes) {
      if (node instanceof Element) unmount(node);
    }
  }
});
removalObserver.observe(document.documentElement, { childList: true, subtree: true });

applyState();
